// The survey on the room cards (SURVEY_CARDS_C1_WORDS.md, MOCKUP_SURVEY_CARDS.html, approved by Robert 2026-10-29
// 8:58 to 9:11 PM). Pure: the registry, the walk and Collect's records in, the cards out.
//
// The one rule: walk the room, open a card, fill it in, move on. The cards are the Assessment Sheet's assets in the
// walk's hanging order. A card's data saves where Collect saves it today (ieaData/{assetId}); Fed From saves the
// same Connections row the Map draws.

import { walkRowsOf } from "./bring-in";
import type { ClassOption } from "./asset-id";
import { blankGear, breakerNames, OUTSIDE, spaceKey, type FieldSite, type Gear } from "./field-labels-site";
import { blanksOf, cardFor, cardStatus, emptyRec, finished, isMccAsset, rollUp, rowFields, recOf, rowRec, rowsOf, rowStatus, type Rec, type Site, type Status } from "./survey-collect";
import { assetKey, codeOf, isSource, isPositionPoint, placeOf, shortName, type MapLink, type MapTree, type Registry } from "./survey-map";

/** One card: an asset on the sheet, and the walk gear it came from when there is one. */
export interface CardItem {
  assetId: string;
  gear: Gear | null;
}

export interface CardRoom {
  /** "ER1", "EA2", "Outside". */
  room: string;
  items: CardItem[];
}

/** The walk room an asset sits in: its room code, or Outside for a utility, generator or anything with no room. */
export function roomOfAsset(a: Parameters<typeof placeOf>[0]): string {
  return placeOf(a).room ?? OUTSIDE;
}

const byId = (x: string, y: string) => x.localeCompare(y, undefined, { numeric: true });

/**
 * The rooms and their cards (items 2 and 3). Walk rooms first, in the walk's order, each with its gear in hanging
 * order. Assets the walk does not hold come at the end of their room in Asset ID order. Rooms only the sheet knows
 * come after, then Outside. Buckets are never their own card: they sit on their housing's drawing.
 */
export function cardRooms(reg: Registry, walk: FieldSite, siteNumber: string, classes: readonly ClassOption[]): CardRoom[] {
  const housings = reg.assets.filter((a) => !reg.isPosition(a.assetId) && !a.isOutOfService);
  const byRoom = new Map<string, string[]>();
  for (const a of housings) {
    const r = roomOfAsset(a);
    byRoom.set(r, [...(byRoom.get(r) ?? []), a.assetId]);
  }
  const gearOf = new Map<string, Gear>();
  const walkOrder = new Map<string, string[]>();
  for (const w of walkRowsOf(walk, siteNumber, classes).rows) {
    if (w.pos || !reg.byId.has(w.id)) continue;
    const g = walk.rooms.find((r) => r.name === w.room)?.gear.find((x) => x.id === w.gearId);
    if (g) gearOf.set(w.id, g);
    walkOrder.set(w.room, [...(walkOrder.get(w.room) ?? []), w.id]);
  }
  const names = [...walk.rooms.map((r) => r.name).filter((n) => n !== OUTSIDE), ...[...byRoom.keys()].filter((n) => n !== OUTSIDE).sort(byId), OUTSIDE];
  const out: CardRoom[] = [];
  for (const name of [...new Set(names)]) {
    const inWalk = (walkOrder.get(name) ?? []).filter((id) => byRoom.get(name)?.includes(id));
    const rest = (byRoom.get(name) ?? []).filter((id) => !inWalk.includes(id)).sort(byId);
    const ids = [...inWalk, ...rest];
    if (ids.length) out.push({ room: name, items: ids.map((assetId) => ({ assetId, gear: gearOf.get(assetId) ?? null })) });
  }
  return out;
}

/** The panel breakers as the walk counts them: B1, B2, by their lowest circuit ("11"), where the survey keeps them. */
export function walkBreakers(g: Gear | null): { key: string; name: string; poles: number }[] {
  if (!g || g.type === "MCC") return [];
  const names = breakerNames(g);
  return Object.entries(g.circuits)
    .map(([c, poles]) => ({ c: Number(c), poles }))
    .filter((x) => names.has(x.c))
    .sort((a, b) => a.c - b.c)
    .map((x) => ({ key: String(x.c), name: names.get(x.c) ?? String(x.c), poles: x.poles }));
}

/** The rows on a card's drawing: a panel's walk breakers, else the registry's buckets and Collect's typed breakers. */
export function cardRows(site: Site, item: CardItem): { key: string; name: string; rec: Rec }[] {
  const br = walkBreakers(item.gear);
  if (br.length) return br.map((b) => ({ key: b.key, name: b.name, rec: recOf(site, item.assetId).breakers[b.key] ?? emptyRec() }));
  return rowsOf(site, item.assetId)
    .filter((row) => row.point)
    .map((row) => ({ key: row.point, name: row.point, rec: rowRec(site, item.assetId, row) }));
}

/**
 * Gear the walk never drew (it came in from the sheet with Asset IDs only): its sections read from the bucket
 * positions (SURVEY_DRAWING_IDS_C1_WORDS.md). Each letter is a section, each number a space top to bottom, a
 * letter after the number a split (B5a, B5b). Null when the positions do not make a clean front (a gap, a stray
 * name), so the card keeps its list.
 */
export function gearFromIds(keys: string[]): Gear | null {
  if (!keys.length) return null;
  const by = new Map<string, Map<number, string[]>>();
  for (const k of keys) {
    const m = /^([A-Z])(\d+)([a-z])?$/.exec(k);
    if (!m) return null;
    const sec = by.get(m[1]!) ?? new Map<number, string[]>();
    const parts = sec.get(Number(m[2])) ?? [];
    parts.push(m[3] ?? "");
    sec.set(Number(m[2]), parts);
    by.set(m[1]!, sec);
  }
  const letters = [...by.keys()].sort();
  const g = blankGear("MCC", "", "ids");
  for (const [i, l] of letters.entries()) {
    const sec = by.get(l)!;
    const top = Math.max(...sec.keys());
    for (let n = 1; n <= top; n++) {
      const parts = sec.get(n);
      if (!parts) return null;
      if (parts.length === 1 && parts[0] === "") continue;
      const want = Array.from({ length: parts.length }, (_, k) => String.fromCharCode(97 + k));
      if (parts.length < 2 || parts.includes("") || [...parts].sort().join() !== want.join()) return null;
      g.splits[spaceKey(i, n)] = parts.length;
    }
    g.sections.push(top);
  }
  return g;
}

/** The section letters of a front built from Asset IDs, left to right. */
export function lettersFromIds(keys: string[]): string[] {
  return [...new Set(keys.map((k) => k[0]!))].sort();
}

/** A row's light on the drawing. */
export function rowLight(site: Site, item: CardItem, key: string): Status {
  const row = cardRows(site, item).find((r) => r.key === key);
  return row ? rowStatus(row.rec, isMccAsset(site, item.assetId)) : "none";
}

/** A card's light (item 5): the housing card and every row on its drawing. */
export function cardLight(site: Site, item: CardItem): Status {
  const s = cardStatus(cardFor(site, item.assetId), recOf(site, item.assetId));
  const rows = cardRows(site, item).map((r) => rowStatus(r.rec, isMccAsset(site, item.assetId)));
  if (!rows.length) return s;
  return rollUp([s, ...rows]);
}

/** A closed card's grey line (item 4): "Panelboard · Housing done · 4 of 25 breakers". */
export function cardLine(site: Site, reg: Registry, item: CardItem): string {
  const a = reg.byId.get(item.assetId);
  const type = a?.equipmentType || cardFor(site, item.assetId).title;
  const light = cardLight(site, item);
  const rows = cardRows(site, item);
  // Blanks: required fields looked at and left empty (SURVEY_DRAWING_IDS_C1_WORDS.md item 9).
  const mcc = isMccAsset(site, item.assetId);
  const card = cardFor(site, item.assetId);
  const hrec = recOf(site, item.assetId);
  const touchedRow = (r: Rec) => Object.keys(r.f).length > 0 || Object.keys(r.photos).length > 0 || r.opened !== "";
  const blanks = (Object.keys(hrec.f).length || Object.keys(hrec.photos).length ? blanksOf(card.fields, hrec) : 0) + rows.reduce((n, r) => n + (touchedRow(r.rec) ? blanksOf(rowFields(mcc), r.rec) : 0), 0);
  const tail = blanks ? ` · ${blanks} blank` : "";
  if (light === "done") return `${type} · Done${tail}`;
  if (light === "missing") return `${type} · Missing Data${tail}`;
  if (light === "none") return `${type} · Not started`;
  const housing = cardStatus(card, hrec);
  if (!rows.length) return `${type} · Started${tail}`;
  const word = mcc ? "buckets" : "breakers";
  const done = rows.filter((r) => finished(rowStatus(r.rec, mcc))).length;
  return `${type} · Housing ${housing === "done" ? "done" : housing === "missing" ? "missing data" : housing === "started" ? "started" : "not started"} · ${done} of ${rows.length} ${word}${tail}`;
}

/** A room is green when every card in it is green (item 5); blue when all finished with any blue. */
export function roomLight(site: Site, room: CardRoom): Status {
  return rollUp(room.items.map((i) => cardLight(site, i)));
}

// ---- Fed From (items 11 to 14) ----

/** The live feed into an asset, the line the Map draws into it. Null: not fed yet. */
export function feedInto(links: readonly MapLink[], assetId: string): MapLink | null {
  return links.find((l) => l.status !== "Removed" && l.goesTo === "Equipment" && l.toAsset === assetId && !isPositionPoint(l.toPoint) && l.toPoint !== "Emergency") ?? null;
}

/** "ER1 · MCC-1A · B2": where a feed comes from, the walk's breaker name when there is one. */
export function feedWords(reg: Registry, l: MapLink, gearOf: (assetId: string) => Gear | null): string {
  const a = reg.byId.get(l.fromAsset);
  if (!a) return l.fromAsset;
  const room = placeOf(a).room;
  const brk = walkBreakers(gearOf(l.fromAsset)).find((b) => b.key === l.fromPoint)?.name;
  // "Out" and "Sec" mean the gear itself: no position to name.
  const self = !l.fromPoint || l.fromPoint === "Out" || l.fromPoint === "Sec";
  return [room, shortName(a), self ? "" : (brk ?? l.fromPoint)].filter(Boolean).join(" · ");
}

/** Utilities are the source: no Fed From (item 14). */
export function takesFeed(reg: Registry, assetId: string): boolean {
  const a = reg.byId.get(assetId);
  return Boolean(a) && !(isSource(a!) && codeOf(a!) !== "GEN");
}

/** The gear that may feed this asset: everything in the room but itself and what it already feeds downstream. */
export function feederChoices(reg: Registry, t: MapTree, room: string, assetId: string): string[] {
  const below = downstream(t, assetId);
  return reg.assets
    .filter((a) => !reg.isPosition(a.assetId) && !a.isOutOfService && roomOfAsset(a) === room && a.assetId !== assetId && !below.has(a.assetId))
    .map((a) => a.assetId)
    .sort(byId);
}

/** Every asset fed, at any depth, from this one on the Map. A feed from one of them would loop. */
export function downstream(t: MapTree, assetId: string): Set<string> {
  const out = new Set<string>();
  const walkDown = (key: string) => {
    for (const k of t.kids.get(key) ?? []) {
      const n = t.nodes.get(k);
      if (!n) continue;
      if (n.kind === "asset" && n.ref !== assetId) out.add(n.ref);
      walkDown(k);
    }
  };
  walkDown(assetKey(assetId));
  return out;
}

/**
 * Where on the feeder the feed leaves (item 11): its buckets, or its walk breakers by name, or its typed breakers.
 * Empty: the feeder has one way out (a transformer, a disconnect), so the pick saves with no position.
 */
export function feederPoints(site: Site, reg: Registry, feeder: string, gear: Gear | null): { key: string; name: string }[] {
  const br = walkBreakers(gear);
  if (br.length) return br.map((b) => ({ key: b.key, name: b.name }));
  const pos = reg.positionsOf(feeder);
  if (pos.length) return pos.map((p) => ({ key: p, name: p }));
  // A straight-out feed draws an empty position on the Map: it is no position to pick.
  return rowsOf(site, feeder)
    .filter((r) => r.point)
    .map((r) => ({ key: r.point, name: r.point }));
}
